import { Injectable, Inject } from '@angular/core';
import { ExpertiseModel } from 'src/app/shared/models/expertisemodel.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { JobGridLookups } from 'src/app/shared/models/job-grid-lookups';
import { Specialty } from 'src/app/shared/models/lookup-profession.model';
import { appendQuery, combine } from '../shared/utilities/route';
import { APP_CONFIG, Settings } from '../config/settings';
import { Observable } from 'rxjs';
import { LineItemTypesModel } from '../shared/models/line-item-types.model';
import { map } from 'rxjs/operators';
import { SystemLookupModel } from 'src/app/core/models/system-lookup.model';
import { UnitLookupModel } from 'src/app/core/models/unit-lookup.model';
import { FacilityLookupModel } from 'src/app/core/models/facility-lookup.model';
import { RelatedExpertiseModel } from './models/related-expertise-lookup.model';
import { VendorSearch } from 'src/app/shared/models/vendor-search.model';
import { FacilityUnitType } from '../shared/models/facility-unit-type.model';
import { SimpleIdName } from '../shared/models/simple-id-name.model';
import { CountryState } from '../shared/models/lookup-country-states.model';
import { PersonPhoneNumberCategory } from '../shared/models/lookup-person.model';
import { FollowupTime } from '../shared/models/followup-time.model';
import { TimecardUnitModel } from '../payroll/timecards/details/models';

@Injectable({
    providedIn: 'root'
})
export class LookupService {
    private readonly coreApiNovaPath: string;
    private readonly coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiNovaPath = combine(settings.coreAPIUrl, 'ayanova');
        this.coreApiPath = combine(settings.coreAPIUrl, 'api');
    }

    getRelatedExpertiseList() {
        return this.http.get<RelatedExpertiseModel[]>(`${this.coreApiNovaPath}/lookup/relatedspecialties`);
    }

    getTimecardUnits(timecardId: number): Observable<TimecardUnitModel[]> {
        const url = `${this.coreApiNovaPath}/Timecard/${timecardId}/units`;
        return this.http.get<TimecardUnitModel[]>(url);
    }

    getProfessionsList() {
        return this.http.get<ExpertiseModel[]>(`${this.coreApiNovaPath}/lookup/professions`);
    }

    getExpertiseList() {
        return this.http.get<ExpertiseModel[]>(`${this.coreApiNovaPath}/lookup/expertises`);
    }

    getSpecialties(): Observable<Specialty[]> {
        const url = `${this.coreApiNovaPath}/Jobs/Lookups`;
        return this.http.get<JobGridLookups>(url).pipe(map((resp) => resp?.specialties || []));
    }

    getExpertisesByProfession(professionId: number) {
        return this.http.get<ExpertiseModel[]>(`${this.coreApiNovaPath}/lookup/professions/${professionId}/expertises`);
    }

    getLineItemTypesList() {
        return this.http.get<LineItemTypesModel[]>(`${this.coreApiNovaPath}/lookup/lineitemtypes`);
    }

    getNurseList(filter: string, companyId?: number) {
        return this.http.get<any>(`${this.coreApiPath}/account/users/search/${filter}?companyId=${companyId}`);
    }

    getVendorList(filter: string) {
        return this.http.get<VendorSearch[]>(`${this.coreApiNovaPath}/Vendor/search?filter=${filter}`);
    }

    getSubcontractorList(filter: string, vendorId?: number) {
        return this.http.get<any>(`${this.coreApiNovaPath}/Vendor/${vendorId}/Candidate/Name?searchStr=${filter}`);
    }

    getCandidateList(filter: string) {
        return this.http.get<any>(`${this.coreApiNovaPath}/Timecard/candidates?searchterm=${filter}`);
    }

    getByNurseId(userId?: number) {
        return this.http.get<any>(`${this.coreApiPath}/account/users/nurse/${userId}`);
    }

    getViewTypesList() {
        return this.http.get<any>(`${this.coreApiNovaPath}/lookup/viewtypes`);
    }

    getLinesOfBusiness() {
        return this.http.get<any>(`${this.coreApiNovaPath}/lookup/lines-of-business`);
    }

    getSystemsList(filter: string, excludeFacilities = false): Observable<SystemLookupModel[]> {
        const url = `${this.coreApiNovaPath}/hospital/system/markers`;
        let params = new HttpParams();
        if (filter) {
            params = params.set('filter', filter);
        }
        if (excludeFacilities) {
            params = params.set('excludeFacilities', `${excludeFacilities}`);
        }
        return (
            this.http
                .get<SystemLookupModel[]>(url, { params })
                // TODO: remove after appropriate naming on the server
                .pipe(map((list) => this.mapSystemLookups(list)))
        );
    }

    getFacilityList(filter: string, excludeUnits?: boolean, systemId?: number): Observable<FacilityLookupModel[]> {
        const url = `${this.coreApiNovaPath}/hospital/markers`;
        let params = new HttpParams();
        if (filter) {
            params = params.set('filter', filter);
        }
        if (excludeUnits) {
            params = params.set('excludeUnits', `${excludeUnits}`);
        }
        if (systemId) {
            params = params.set('hospitalSystemCode', `${systemId}`);
        }
        return (
            this.http
                .get<FacilityLookupModel[]>(url, { params })
                // TODO: remove after appropriate naming on the server
                .pipe(map((list) => this.mapFacilityLookups(list)))
        );
    }

    getUnitList(systemId: number, facilityId: number, filter: string, take?: number): Observable<UnitLookupModel[]> {
        let requestUrl = `${this.coreApiNovaPath}/hospital/unit/markers`;
        if (systemId) {
            requestUrl = appendQuery(requestUrl, `hospitalSystemCode=${systemId}`);
        }
        if (facilityId) {
            requestUrl = appendQuery(requestUrl, `hospitalId=${facilityId}`);
        }
        if (filter) {
            requestUrl = appendQuery(requestUrl, `filter=${filter}`);
        }
        if (take) {
            requestUrl = appendQuery(requestUrl, `take=${take}`);
        }

        return (
            this.http
                .get<UnitLookupModel[]>(requestUrl)
                // TODO: remove after appropriate naming on the server
                .pipe(map((list) => this.mapUnitLookups(list)))
        );
    }

    getFilteredUnitList(
        systemId: number,
        facilityId: number,
        filter: string,
        take?: number
    ): Observable<UnitLookupModel[]> {
        let requestUrl = `${this.coreApiNovaPath}/hospital/unit/filtered-markers`;
        if (systemId) {
            requestUrl = appendQuery(requestUrl, `hospitalSystemCode=${systemId}`);
        }
        if (facilityId) {
            requestUrl = appendQuery(requestUrl, `hospitalId=${facilityId}`);
        }
        if (filter) {
            requestUrl = appendQuery(requestUrl, `filter=${filter}`);
        }
        if (take) {
            requestUrl = appendQuery(requestUrl, `take=${take}`);
        }

        return (
            this.http
                .get<UnitLookupModel[]>(requestUrl)
                // TODO: remove after appropriate naming on the server
                .pipe(map((list) => this.mapUnitLookups(list)))
        );
    }

    getUnitTypes(): Observable<SimpleIdName[]> {
        return this.http.get<SimpleIdName[]>(`${this.coreApiNovaPath}/ClientServices/Lookups/UnitTypes`);
    }

    getFacilityUnitTypes(): Observable<FacilityUnitType[]> {
        return this.http.get<FacilityUnitType[]>(`${this.coreApiNovaPath}/ClientServices/unit-types`);
    }

    getBlockedDomains(): Observable<SimpleIdName[]> {
        return this.http.get<SimpleIdName[]>(`${this.coreApiNovaPath}/lookup/blocked-domains`);
    }

    getFollowupTimeEntries(): Observable<FollowupTime[]> {
        const url = `${this.coreApiNovaPath}/lookup/followup-time-codes`;
        return this.http.get<FollowupTime[]>(url);
    }

    getCountryStates(): Observable<CountryState[]> {
        return this.http.get<CountryState[]>(`${this.coreApiNovaPath}/lookup/country-states`);
    }

    getPersonPhoneNumberCategoriesLookup(): Observable<PersonPhoneNumberCategory[]> {
        const url = `${this.coreApiNovaPath}/persons/phone-number-categories`;
        return this.http.get<PersonPhoneNumberCategory[]>(url);
    }

    // TODO: remove after appropriate naming on the server
    private mapSystemLookups(systems: SystemLookupModel[]): SystemLookupModel[] {
        if (!systems) {
            return [];
        }

        return systems.map((value) => {
            value.id = value.hospitalSystemCode;
            value.hospitals = this.mapFacilityLookups(value.hospitals);
            return value;
        });
    }

    private mapFacilityLookups(facilitites: FacilityLookupModel[]): FacilityLookupModel[] {
        if (!facilitites) {
            return [];
        }

        return facilitites.map((value) => {
            value.id = value.hospId;
            value.name = value.hospName;
            value.systemId = value.hospitalSystemCode;
            value.systemName = value.hospitalSystemName;
            value.units = this.mapUnitLookups(value.units);
            return value;
        });
    }

    private mapUnitLookups(units: UnitLookupModel[]): UnitLookupModel[] {
        if (!units) {
            return [];
        }

        return units.map((value) => {
            value.facilityId = value.hospId;
            return value;
        });
    }
}
